import React from 'react';

import Cropped from './Cropped';

import { StyledBadge } from '../styled';
import { formatFieldString } from '../helpers';

import { useTheme } from 'src/useTheme';

const normalizeStatus = (value: string) => value.toString().trim().toUpperCase().replace(/_/g, ' ');

const Badge = ({ value }: { value: string | boolean }) => {
  const theme = useTheme();

  if (typeof value === 'boolean') {
    const label = value ? 'Verified' : 'Unverified';
    const color = value ? theme.positive : theme.negative;
    const background = value ? theme.positive90 : theme.negative90;
    return (
      <StyledBadge $color={color} $background={background}>
        <span>{label}</span>
      </StyledBadge>
    );
  }

  const normalized = normalizeStatus(value || '');

  // Plain text — no pill (matches "Not required" in the design)
  if (normalized === 'NOT REQUIRED' || normalized === '-') {
    return <Cropped value={formatFieldString(value)} />;
  }

  const STYLES: Record<string, { color: string; background: string }> = {
    ACTIVE: { color: theme.positive, background: theme.positive90 },
    SUCCESS: { color: theme.positive, background: theme.positive90 },
    FULFILLED: { color: theme.positive, background: theme.positive90 },
    COMPLETED: { color: theme.positive, background: theme.positive90 },
    COMPLETE: { color: theme.positive, background: theme.positive90 },
    VERIFIED: { color: theme.positive, background: theme.positive90 },
    SENT: { color: theme.positive, background: theme.positive90 },
    RELEASED: { color: theme.positive, background: theme.positive90 },
    'LOW RISK': { color: theme.positive, background: theme.positive90 },
    'KYC ACCEPTED': { color: theme.positive, background: theme.positive90 },
    TRUE: { color: theme.positive, background: theme.positive90 },

    FAILED: { color: theme.negative, background: theme.negative90 },
    REJECTED: { color: theme.negative, background: theme.negative90 },
    CANCELLED: { color: theme.negative, background: theme.negative90 },
    CANCELED: { color: theme.negative, background: theme.negative90 },
    UNVERIFIED: { color: theme.negative, background: theme.negative90 },
    'NOT SENT': { color: theme.negative, background: theme.negative90 },
    'HIGH RISK': { color: theme.negative, background: theme.negative90 },
    BANNED: { color: theme.negative, background: theme.negative90 },
    SUSPENDED: { color: theme.negative, background: theme.negative90 },
    SUSPICIOUS: { color: theme.negative, background: theme.negative90 },
    FALSE: { color: theme.negative, background: theme.negative90 },

    PENDING: { color: theme.middle, background: theme.middle90 },
    PROCESSING: { color: theme.middle, background: theme.middle90 },
    'UNDER REVIEW': { color: theme.middle, background: theme.middle90 },
    'IN PROGRESS': { color: theme.middle, background: theme.middle90 },
    'APPROVAL PENDING': { color: theme.middle, background: theme.middle90 },
    HELD: { color: theme.middle, background: theme.middle90 },
    FROZEN: { color: theme.middle, background: theme.middle90 },
    'NOT ACTIVATED': { color: theme.middle, background: theme.middle90 },
    INACTIVE: { color: theme.midGrey, background: theme.lightGrey },
  };

  const style = STYLES[normalized];

  if (!style) {
    return <Cropped value={formatFieldString(value)} />;
  }

  let label = formatFieldString(value);
  if (normalized === 'APPROVAL PENDING') label = 'Pending';
  if (normalized === 'SUSPENDED') label = 'Suspicious';
  if (normalized === 'UNVERIFIED') label = 'Not Verified';
  if (normalized === 'NOT ACTIVATED') label = 'Not activated';
  if (normalized === 'VERIFIED') label = 'Verified';
  if (normalized === 'KYC ACCEPTED') label = 'KYC accepted';

  return (
    <StyledBadge $color={style.color} $background={style.background}>
      <span>{label}</span>
    </StyledBadge>
  );
};

export default Badge;
